import { useCallback, useEffect, useRef, useState } from 'react';

export interface LoadState<T> {
  data: T | null;
  loading: boolean;
  /** The caught error from the last load; kept until a load succeeds so a failure never looks like an empty list. */
  error: unknown;
  /** User-initiated (re)load: shows loading, and sets `error` on failure. */
  reload: () => Promise<void>;
  /** Replace the data without touching loading/error (a background refresh landing). */
  setData: (data: T) => void;
}

/**
 * One loader for a screen: runs on mount and whenever `deps` change, keeps the
 * error for an error box with Retry, and ignores a response that arrives after
 * a newer load started or the screen unmounted.
 */
export function useLoad<T>(loader: () => Promise<T>, deps: ReadonlyArray<unknown>): LoadState<T> {
  const [data, setDataState] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);
  const run = useRef(0);
  const loaderRef = useRef(loader);
  loaderRef.current = loader;

  const reload = useCallback(async () => {
    const mine = ++run.current;
    setLoading(true);
    setError(null);
    try {
      const result = await loaderRef.current();
      if (run.current === mine) setDataState(result);
    } catch (err) {
      if (run.current === mine) setError(err ?? new Error('load failed'));
    } finally {
      if (run.current === mine) setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  useEffect(() => {
    void reload();
    return () => { run.current += 1; };
  }, [reload]);

  const setData = useCallback((next: T) => { setDataState(next); }, []);
  return { data, loading, error, reload, setData };
}
